type DailyValueType = 'current' | 'previous' | 'past';

interface DailyItem {
  name: string;
  days: { [key: number]: number };
  previousMonthTotal: number;
  currentPastMonthTotal: number;
}

interface DealerData {
  name: string;
  models: string[];
  items: DailyItem[];
}

const DEFAULT_NA = 'N/A';
const DECIMAL_PRECISION = 2;
const PERCENTAGE_PRECISION = 1;

const ItemType = {
  WALK_IN: 'WALK IN',
  TEST_DRIVE: 'TEST DRIVE',
} as const;

type CalculationConfig = {
  valueType: DailyValueType;
  useCurrentMonthTotal?: boolean;
};

export const calculateTotal = (days: { [key: number]: number }): number => {
  return Object.values(days).reduce((sum, val) => sum + val, 0);
};

const calculatePercentageChange = (
  numerator: number,
  denominator: number
): string => {
  if (denominator === 0) return DEFAULT_NA;
  const percentage = ((numerator - denominator) / denominator) * 100;
  return `${percentage.toFixed(PERCENTAGE_PRECISION)}%`;
};

export const calculateVariation = (item: DailyItem): string => {
  const current = calculateTotal(item.days);
  return calculatePercentageChange(current, item.previousMonthTotal);
};

export const calculateVariation2 = (item: DailyItem): string => {
  const current = calculateTotal(item.days);
  return calculatePercentageChange(current, item.currentPastMonthTotal);
};

const getItemValue = (item: DailyItem, config: CalculationConfig): number => {
  switch (config.valueType) {
    case 'current':
      return calculateTotal(item.days);
    case 'previous':
      return item.previousMonthTotal;
    case 'past':
      return item.currentPastMonthTotal;
    default:
      return 0;
  }
};

const calculateRatio = (
  dealer: DealerData,
  config: CalculationConfig
): string => {
  let sumNonBase = 0;
  let walkInTotal = 0;

  dealer.items.forEach(item => {
    const value = getItemValue(item, config);

    if (item.name.toUpperCase() === ItemType.WALK_IN) {
      walkInTotal = value;
    } else if (item.name.toUpperCase() !== ItemType.TEST_DRIVE) {
      sumNonBase += value;
    }
  });

  if (walkInTotal === 0) return DEFAULT_NA;
  return (sumNonBase / walkInTotal).toFixed(DECIMAL_PRECISION);
};

export const calculateCurrentMonthACT = (dealer: DealerData): string =>
  calculateRatio(dealer, { valueType: 'current' });

export const calculatePreviousMonthACT = (dealer: DealerData): string =>
  calculateRatio(dealer, { valueType: 'previous' });

export const calculateCurrentYearMinusOnePSDO = (dealer: DealerData): string =>
  calculateRatio(dealer, { valueType: 'past' });